/**
 * What clicking Close on an open position should actually do.
 *
 * One contract can be held by more than one session. The venue shows a single merged
 * position and cannot tell the legs apart, so closing is only expressible per session —
 * and that means Close is not one action but a question: whose leg?
 *
 * The rule lives here because getting it wrong is expensive in one direction only.
 * Closing one session's leg when the user meant all of them leaves money on the exchange,
 * which they can see and fix. Closing every session when they meant one takes a position
 * they wanted to keep. So a shared contract never closes without being asked.
 */

import i18n from "@/i18n";

export interface ClosableSession {
  subscription_id: number;
  trader_name?: string;
  size?: number;
  share?: number;
  unrealized_pnl?: number;
}

export type CloseIntent =
  /** Nothing to act on. */
  | { kind: "none" }
  /** One session holds it: confirm, then close that one. */
  | { kind: "single"; subscriptionId: number }
  /** More than one session holds it: ask which, or all. */
  | { kind: "choose"; sessions: ClosableSession[] };

/**
 * `sessions` is the attribution the tray already shows. A position the venue reports but
 * we have attributed to nobody yields "none": we cannot close what we cannot name, and
 * offering a button that would fail is worse than offering none.
 */
export function closeIntentFor(sessions: ClosableSession[] | undefined | null): CloseIntent {
  const list = (sessions ?? []).filter((s) => Number.isFinite(s?.subscription_id) && s.subscription_id > 0);
  if (list.length === 0) return { kind: "none" };
  if (list.length === 1) return { kind: "single", subscriptionId: list[0].subscription_id };
  return { kind: "choose", sessions: list };
}

/**
 * The subscriptions a chosen option should close.
 *
 * `all` is every session holding the contract, closed one call each, because the server
 * has no merged close and should not have one.
 */
export function subscriptionsToClose(
  sessions: ClosableSession[],
  choice: "all" | number,
): number[] {
  if (choice === "all") return sessions.map((s) => s.subscription_id);
  return sessions.some((s) => s.subscription_id === choice) ? [choice] : [];
}

/**
 * What to tell the user after closing several legs at once.
 *
 * Partial failure is the case that matters: three legs sent, two closed, and the third
 * still open on the exchange. Reporting "closed" there is the one unacceptable answer, so
 * any failure at all makes the whole report a failure and names the survivors.
 */
/****************
 * "IT FAILED" AND "WE DO NOT KNOW" ARE DIFFERENT ANSWERS.
 *
 * Guillaume, 2026-09-14: closing from the tray returned a Cloudflare 502 and
 * the toast read "Nothing was closed."
 *
 * That sentence may have been FALSE. The close request had already reached the
 * exchange -- our own server killed the connection at its write timeout while
 * the handler was still waiting for the fill -- so the position was very
 * possibly closed while the user was told it was not. Someone reading that
 * toast closes it again, or walks away from a position they think is still
 * open.
 *
 * `unknown` marks the result of a call that never came back from OUR server.
 * The handler always answers {"error": "<sentence>"}; anything else came from
 * in front of it, and means only that we did not hear back.
 ****************/
export function closeOutcome(
  results: Array<{ subscriptionId: number; ok: boolean; error?: string; unknown?: boolean }>,
): { ok: boolean; message: string } {
  const failed = results.filter((r) => !r.ok);
  if (results.length === 0) return { ok: false, message: i18n.t("messages:close.nothing") };
  if (failed.length === 0) {
    return {
      ok: true,
      message: results.length === 1
        ? i18n.t("messages:close.one")
        : i18n.t("messages:close.all", { n: results.length }),
    };
  }

  /*
   * Any unknown at all makes the whole answer unknown. A run where one session
   * refused and another never answered cannot be summarised as a failure --
   * the second one may have gone through.
   */
  const unknown = failed.filter((f) => f.unknown);
  if (unknown.length > 0) {
    return {
      ok: false,
      message:
        results.length === 1
          ? i18n.t("messages:close.unknown")
          : i18n.t("messages:close.unknownWhere", { sessions: sessionList(unknown) }),
    };
  }

  const survivors = sessionList(failed);
  return {
    ok: false,
    message:
      failed.length === results.length
        ? i18n.t("messages:close.nothingWith", { error: failed[0].error ?? "" }).trim()
        : i18n.t("messages:close.partial", {
            closed: results.length - failed.length,
            total: results.length,
            sessions: survivors,
          }),
  };
}

/** "session 12, session 14" */
function sessionList(rows: Array<{ subscriptionId: number }>): string {
  return rows.map((f) => i18n.t("messages:close.session", { id: f.subscriptionId })).join(", ");
}

/****************
 * TURNING A THROWN ERROR INTO A RESULT, WHERE IT CAN BE TESTED.
 *
 * This was three lines inline in the dialog, which is why the field it sets was
 * missing from the array's type and rejected at the boundary: nothing ever
 * exercised the mapping, so nothing noticed that "we could not say" was being
 * dropped on the way to the message.
 *
 * `answeredByServer === false` is the whole rule. Our handlers always reply
 * {"error": "<sentence>"}; anything else came from in front of them and tells
 * us only that we never heard back — not that nothing happened.
 */
export function closeResultFor(
  subscriptionId: number,
  e: { message?: string; answeredByServer?: boolean } | null | undefined,
): { subscriptionId: number; ok: false; error: string; unknown: boolean } {
  return {
    subscriptionId,
    ok: false,
    error: e?.message || i18n.t("messages:close.noAnswer"),
    /*
     * Strictly === false. `undefined` means something threw that is not one of
     * ours — a bug in our own code, say — and guessing "unknown" there would
     * tell the user to go and check the broker over a client-side fault.
     */
    unknown: e?.answeredByServer === false,
  };
}

/**
 * `?side=LONG` / `?side=SHORT` for the close route, or nothing when the row
 * names no side. 2026-10-02: a session can hold both sides of one contract, as
 * two rows, and Close on one of them must not close the other.
 */
export function closeSideQuery(side?: string | null, pct?: number | null): string {
  const s = (side ?? '').trim().toUpperCase();
  const q: string[] = [];
  if (s === 'LONG' || s === 'SHORT') q.push(`side=${s}`);
  // Part of the position (2026-10-14). Whole when left out or 100.
  if (pct != null && Number.isFinite(pct) && pct > 0 && pct < 100) q.push(`pct=${Math.round(pct * 100) / 100}`);
  return q.length ? `?${q.join('&')}` : '';
}

/** The percent picker's quick choices. */
export const CLOSE_PERCENTS = [25, 50, 75, 100] as const;
